/**
 * useEventReminders — in-browser 리마인더 hook (Phase 1 경량)
 *
 * per-user 모델 (2026-06-03): 리마인더는 일정 속성이 아니라 각 사용자의 개인 알림.
 * - remindersMap (event_id → minutes[]) 을 기반으로 정확한 시각에 fire
 *   (event_reminders 테이블에서 본인 행만 fetch 한 것 — RLS 보장)
 * - 반복 일정 인스턴스 id "origId::iso" 는 origId 로 map lookup
 * - 페이지가 열려있을 때만 동작 (Browser Notification API)
 * - 발사 후 sessionStorage 에 fired key 기록 → 중복 방지
 *
 * Phase 2 에서는 Cloud Tasks + FCM 푸시로 교체 — event_reminders 데이터는 그대로 사용
 */
import { useEffect, useRef } from 'react';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { CalendarEvent } from '../../models/CalendarEvent';
import { isPWA } from '../../pwa-push';

const FIRED_KEY = 'calendar-fired-reminders';

/** 발사 기록 set (sessionStorage 백업) */
function loadFired(): Set<string> {
  try {
    const raw = sessionStorage.getItem(FIRED_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch { return new Set(); }
}

function saveFired(set: Set<string>): void {
  try {
    sessionStorage.setItem(FIRED_KEY, JSON.stringify(Array.from(set)));
  } catch {}
}

/** 알림 발사 */
function fireReminder(event: CalendarEvent, minutesBefore: number) {
  if (!isPWA()) return;
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;

  const when = minutesBefore === 0
    ? t`now`
    : minutesBefore < 60 ? t`in ${minutesBefore} min`
      : minutesBefore < 1440 ? t`in ${Math.floor(minutesBefore / 60)} h`
      : t`in ${Math.floor(minutesBefore / 1440)} d`;
  const title = `📅 ${event.title} — ${when}`;
  const body = event.location
    ? `${event.location}\n${new Date(event.start_at).toLocaleString(i18n.locale || 'en')}`
    : new Date(event.start_at).toLocaleString(i18n.locale || 'en');
  try {
    new Notification(title, { body, tag: `event-${event.id}-${minutesBefore}` });
  } catch (err) {
    console.warn('[useEventReminders] Notification failed:', err);
  }
}

/** Notification 권한 요청 — 한 번만 */
async function requestPermission() {
  if (!isPWA()) return;
  if (typeof Notification === 'undefined') return;
  if (Notification.permission === 'default') {
    try {
      await Notification.requestPermission();
    } catch {}
  }
}

/**
 * events 가 변할 때마다 다음 1시간 내 fire 해야 할 리마인더에 대해 setTimeout 예약.
 * unmount/events 변경 시 모든 timeout clear.
 */
export function useEventReminders(
  events: CalendarEvent[],
  remindersMap: Record<string, number[]>,
): void {
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const firedRef = useRef<Set<string>>(loadFired());

  useEffect(() => {
    requestPermission();
  }, []);

  useEffect(() => {
    // 기존 timeout 정리
    for (const t of timeoutsRef.current) clearTimeout(t);
    timeoutsRef.current = [];

    if (typeof Notification === 'undefined') return;

    const now = Date.now();
    const LOOKAHEAD_MS = 60 * 60 * 1000; // 다음 1시간 내만 예약

    for (const event of events) {
      if (event.archived_at) continue;
      // 반복 인스턴스 id "origId::iso" → origId 로 내 리마인더 lookup
      const baseId = event.id.split('::')[0];
      const mins = remindersMap[baseId];
      if (!mins || mins.length === 0) continue;
      const startTs = new Date(event.start_at).getTime();
      for (const m of mins) {
        const fireAt = startTs - m * 60_000;
        const delay = fireAt - now;
        const firedKey = `${event.id}::${m}::${event.start_at}`;
        if (firedRef.current.has(firedKey)) continue;
        if (delay < -60_000) continue; // 1분 이상 지난 건 skip
        if (delay > LOOKAHEAD_MS) continue;

        const fire = () => {
          if (firedRef.current.has(firedKey)) return;
          firedRef.current.add(firedKey);
          saveFired(firedRef.current);
          fireReminder(event, m);
        };

        if (delay <= 0) {
          // 이미 지났지만 1분 이내 — 즉시 fire
          fire();
        } else {
          const t = setTimeout(fire, delay);
          timeoutsRef.current.push(t);
        }
      }
    }

    return () => {
      for (const t of timeoutsRef.current) clearTimeout(t);
      timeoutsRef.current = [];
    };
  }, [events, remindersMap]);
}
